/**
 * Where an account switch lands (ADR-0302, Decision 5): the section of the
 * current page, never an object of the previous account.
 *
 * `/activation/segments/seg_1/edit` → `/activation/segments`. Derived from the
 * matched route PATTERN (`matchRoutes` over the tenant route objects), not by
 * slicing the URL, so a new route gets the right landing without an entry
 * anywhere. `useMatches` is not an option: `switchAccount` calls this outside
 * React, and even inside, the app's data router has one splat route
 * (ADR-0386), so its matches never name a page; the page patterns live in the
 * descendant `<Routes>`.
 *
 * The route objects are registered by `App.tsx` (which owns the route table)
 * instead of imported, so this module stays free of the page graph and of the
 * auth context.
 */

import { matchRoutes, type RouteObject } from "react-router-dom";

import { stripTenantPrefix } from "@/routing/tenantPaths";

let registeredRoutes: RouteObject[] = [];

export function registerTenantRoutes(routes: RouteObject[]): void {
  registeredRoutes = routes;
}

/** Every static (param-free) pattern of the table, as `/a/b`, plus `/` for the index. */
function staticPatterns(routes: RouteObject[], base = ""): Set<string> {
  const out = new Set<string>();
  for (const route of routes) {
    const path = route.index ? base : route.path ? `${base}/${route.path}` : base;
    if (route.index) out.add(path || "/");
    if (route.path) {
      // `chat/:conversationId?` also matches plain `chat`: optional params drop out.
      const required = path
        .split("/")
        .filter((segment) => !/^:[^/]+\?$/.test(segment))
        .join("/");
      if (!/[:*]/.test(required)) out.add(required || "/");
    }
    if (route.children) {
      for (const child of staticPatterns(route.children, path)) out.add(child);
    }
  }
  return out;
}

/**
 * The section root of a tenant path (prefixed or not), as an unprefixed path.
 * `/` means the account's landing page.
 */
export function sectionRoot(pathname: string, routes: RouteObject[] = registeredRoutes): string {
  const tenantPathname = stripTenantPrefix(pathname);
  const matches = matchRoutes(routes, tenantPathname);
  const leaf = matches?.at(-1);
  if (!leaf || !leaf.route.path || /^\*$/.test(leaf.route.path)) return "/";

  // Rebuild the matched pattern from every matched level.
  const pattern = matches!
    .map((m) => m.route.path)
    .filter((p): p is string => Boolean(p))
    .join("/");
  const segments = pattern.split("/").filter(Boolean);
  const firstDynamic = segments.findIndex((s) => s.startsWith(":") || s === "*");
  const kept = firstDynamic === -1 ? segments : segments.slice(0, firstDynamic);

  // The nearest ancestor that is itself a route; never the catch-all.
  const existing = staticPatterns(routes);
  for (let n = kept.length; n > 0; n -= 1) {
    const candidate = `/${kept.slice(0, n).join("/")}`;
    if (existing.has(candidate)) return candidate;
  }
  return "/";
}
